<template>
  <!-- My BF Fulfilment's ring from 2026 on, as the F frames draw it: a 200px ring on a
       beige track with round ends, the label and amount inside. ProgressTracker is the
       earlier years' ring and keeps its own look. -->
  <div class="relative size-[200px] md:size-[250px] lg:size-[300px]">
    <!-- F3, a sow alone: a thinner green ring, always full since a sow has no target,
         that draws itself and then grows its leaves. -->
    <svg v-if="sow" viewBox="0 0 120 120" class="size-full overflow-visible" aria-hidden="true">
      <circle cx="60" cy="60" r="50" fill="none" stroke-width="6" class="stroke-sow-track" />
      <circle
        cx="60"
        cy="60"
        r="50"
        fill="none"
        stroke-width="6"
        stroke-linecap="round"
        :stroke-dasharray="`${circumference} ${circumference}`"
        transform="rotate(-90 60 60)"
        class="stroke-sow-ring motion-safe:animate-bf-ring-draw"
        data-test="bf-fulfilment-ring-progress"
      />
      <g v-for="(leaf, index) in leaves" :key="index" :transform="leaf.transform">
        <g
          :class="[
            'origin-left [transform-box:fill-box] motion-safe:animate-bf-leaf-pop',
            leaf.delay,
          ]"
          data-test="bf-fulfilment-ring-leaf"
        >
          <path
            d="M0 0 C3 -4.6 9 -4.6 12 0 C9 4.6 3 4.6 0 0Z"
            :class="index % 2 ? 'fill-sow-leaf-dark' : 'fill-sow-leaf'"
          />
          <path d="M1 0 H9" stroke-width=".7" stroke-linecap="round" class="stroke-sow-vein" />
        </g>
      </g>
    </svg>
    <svg v-else viewBox="0 0 120 120" class="size-full" aria-hidden="true">
      <circle cx="60" cy="60" r="50" fill="none" stroke-width="12" class="stroke-brown-track" />
      <!-- Left out at 0%: a round cap would still draw a dot. -->
      <circle
        v-if="progress > 0"
        cx="60"
        cy="60"
        r="50"
        fill="none"
        stroke-width="12"
        stroke-linecap="round"
        :stroke-dasharray="`${(circumference * progress) / 100} ${circumference}`"
        transform="rotate(-90 60 60)"
        class="stroke-orange-500"
        data-test="bf-fulfilment-ring-progress"
      />
    </svg>
    <div class="absolute inset-0 flex flex-col items-center justify-center gap-[2px] text-center">
      <span
        class="text-[15px] md:text-[18px] lg:text-[22px] leading-[20px] md:leading-[24px] lg:leading-[28px] text-grey-800 whitespace-pre-line"
        >{{ label }}</span
      >
      <span
        :class="[
          'font-extrabold text-prime-ink',
          large
            ? 'text-[26px] md:text-[32px] lg:text-[38px] leading-[32px] md:leading-[40px] lg:leading-[46px]'
            : 'text-[20px] md:text-[25px] lg:text-[30px] leading-[28px] md:leading-[34px] lg:leading-[40px]',
        ]"
        data-test="bf-fulfilment-ring-amount"
        >{{ formatAmount(fulfilled) }}</span
      >
    </div>
  </div>
</template>

<script>
import { formatPledgeAmount } from '@/utils/building-fund-pledge';

// F3's twelve leaves, as the frame places them: each sits on the ring, turned out from
// it, alternately full size and two-thirds, and pops out after the one before it once
// the ring has drawn. The delays are whole class names so Tailwind generates them.
const LEAVES = [
  ['translate(73.59 9.29) rotate(-38.1)', 'motion-safe:[animation-delay:2.19s]'],
  ['translate(93.59 26.41) rotate(98.1) scale(0.67)', 'motion-safe:[animation-delay:2.27s]'],
  ['translate(110.71 46.41) rotate(21.9)', 'motion-safe:[animation-delay:2.34s]'],
  ['translate(105.88 72.29) rotate(158.1) scale(0.67)', 'motion-safe:[animation-delay:2.41s]'],
  ['translate(97.12 97.12) rotate(81.9)', 'motion-safe:[animation-delay:2.49s]'],
  ['translate(72.29 105.88) rotate(-141.9) scale(0.67)', 'motion-safe:[animation-delay:2.56s]'],
  ['translate(46.41 110.71) rotate(141.9)', 'motion-safe:[animation-delay:2.63s]'],
  ['translate(26.41 93.59) rotate(-81.9) scale(0.67)', 'motion-safe:[animation-delay:2.71s]'],
  ['translate(9.29 73.59) rotate(-158.1)', 'motion-safe:[animation-delay:2.79s]'],
  ['translate(14.12 47.71) rotate(-21.9) scale(0.67)', 'motion-safe:[animation-delay:2.86s]'],
  ['translate(22.88 22.88) rotate(-98.1)', 'motion-safe:[animation-delay:2.94s]'],
  ['translate(47.71 14.12) rotate(38.1) scale(0.67)', 'motion-safe:[animation-delay:3.01s]'],
].map(([transform, delay]) => ({ transform, delay }));

export default {
  name: 'FulfilmentRing',

  props: {
    total: {
      type: Number,
      default: 0,
    },
    fulfilled: {
      type: Number,
      default: 0,
    },
    // Inside the ring. A line break in it is kept.
    label: {
      type: String,
      required: true,
    },
    // F2 (a pledge beside a sow) draws the amount at 26px; F1 and F4 at 20px.
    large: {
      type: Boolean,
      default: false,
    },
    // F3, a sow alone: the full green ring with leaves, whatever the amounts.
    sow: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      circumference: 2 * Math.PI * 50,
      leaves: LEAVES,
    };
  },

  computed: {
    // Rounded to a whole percent, and capped at 100 when more was given than pledged.
    progress() {
      if (!this.total) return 0;
      return Math.min(Math.round((this.fulfilled / this.total) * 100), 100);
    },
  },

  methods: {
    formatAmount: formatPledgeAmount,
  },
};
</script>
